import React from 'react';
import styled, { css } from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../i18n/TModules';
import { orbitTokens } from '../../styles';
import { ResponsePanel, ResponseReply } from '../../support/response/responsePrimitives';
import { rsColor } from '../../support/response/responseScale';
import { OrbitActionCandidate } from '../types';

// 6566:9934: the title 12 in and 11 down, Inter Bold 15.
const Title = styled.h4`
  margin: 0;
  padding: 11px 12px 0;
  color: ${rsColor.text};
  font-size: 15px;
  font-weight: 700;
  line-height: 18px;
  white-space: pre-line;
`;

const Table = styled.div`
  padding: 13px 11px 12px;
`;

// Columns at 58 / 169 against the card's 42, and the id cell 7 right of its head.
const columns = css`
  display: grid;
  grid-template-columns: 104px minmax(0, 1fr) auto;
  align-items: center;
  column-gap: 8px;
`;

// 6566:9874: Bold 11.5, 0.57 tracking, upper case. The drawn #64709A fails AA here, so the muted text
// token stands in for it.
const Head = styled.div`
  ${columns}
  padding: 0 5px 10px;
  border-bottom: 1px solid ${rsColor.border};
  color: ${orbitTokens.textMuted};
  font-size: 11.5px;
  font-weight: 700;
  line-height: 14px;
  letter-spacing: 0.57px;
  text-transform: uppercase;
`;

// 43 px pitch, ruled between rows and not under the last.
const Candidate = styled.button`
  ${columns}
  width: 100%;
  min-height: 43px;
  padding: 0 5px 0 12px;
  border: none;
  border-bottom: 1px solid ${rsColor.border};
  background: transparent;
  font-size: 13.5px;
  line-height: 16px;
  text-align: left;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:hover:not(:disabled),
  &:focus-visible {
    background: rgba(38, 138, 255, 0.08);
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const Id = styled.span`
  color: ${rsColor.text};
  font-weight: 700;
`;

const Name = styled.span`
  min-width: 0;
  color: ${rsColor.secondary};
  overflow-wrap: anywhere;
`;

const State = styled.span`
  color: ${rsColor.secondary};
  font-size: 11.5px;
  text-transform: capitalize;
`;

interface IL4CandidateListProps {
  text: string;
  candidates: OrbitActionCandidate[];
  /** The pick is open and nothing is in flight. */
  selectable: boolean;
  onSelect: (candidate: OrbitActionCandidate) => void;
}

/** The picker: one button per candidate; an ineligible one stays listed, greyed, with its reason. */
const L4CandidateList: React.FC<IL4CandidateListProps> = ({
  text,
  candidates,
  selectable,
  onSelect
}) => {
  const { t } = useMultiLanguageTranslation();

  return (
    <ResponseReply data-testid="orbit-l4-picker" data-status={selectable ? 'open' : 'locked'}>
      <ResponsePanel>
        <Title data-testid="orbit-l4-text">{text}</Title>
        <Table role="group" aria-label={text}>
          <Head aria-hidden="true">
            <span>{t(`${TOrbit}.layer4.picker.id`)}</span>
            <span>{t(`${TOrbit}.layer4.picker.name`)}</span>
            <span>{t(`${TOrbit}.layer4.picker.state`)}</span>
          </Head>
          {candidates.map(candidate => (
            <Candidate
              key={`${candidate.step_id}-${candidate.id}`}
              type="button"
              disabled={!selectable || !candidate.eligible}
              data-testid="orbit-l4-candidate"
              data-eligible={String(candidate.eligible)}
              onClick={() => onSelect(candidate)}
            >
              <Id>{candidate.id}</Id>
              <Name>{candidate.name}</Name>
              <State>
                {candidate.eligible ? candidate.state : candidate.reason ?? candidate.state}
              </State>
            </Candidate>
          ))}
        </Table>
      </ResponsePanel>
    </ResponseReply>
  );
};

export default L4CandidateList;
